import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Container,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import Header from "../../../components/Header/Header";
import useAxios from "../../../hooks/useAxios";
import "./AIGeneratedQuizPage.css";

const navLinks = [];

// Chat message types
const MESSAGE_TYPES = {
  AI: "ai",
  USER: "user",
  ERROR: "error",
  LOADING: "loading",
};

// Conversation steps
const CONVERSATION_STEPS = {
  TITLE: "title",
  QUESTION_COUNT: "question_count",
  QUESTION_TYPE: "question_type",
  LANGUAGE: "language",
  REQUIREMENTS: "requirements",
  COMPLETE: "complete",
};

function AIGeneratedQuizPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const axiosInstance = useAxios();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Quiz data
  const [quizData, setQuizData] = useState({
    title: "",
    questionCount: 10,
    questionType: "",
    language: "",
    requirements: "",
  });

  // Language selection state
  const [languageStep, setLanguageStep] = useState("select"); // "select" or "custom"

  // Chat state
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState(CONVERSATION_STEPS.TITLE);
  const [inputValue, setInputValue] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [maxRetries] = useState(2);

  // Get classId from query params
  const queryParams = new URLSearchParams(location.search);
  const classId = queryParams.get("classId");

  const scrollToBottom = () => {
    // Add a small delay to ensure the message is fully rendered
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 100);
  };

  useEffect(() => {
    // Only scroll to bottom when new AI messages are added
    if (
      messages.length > 0 &&
      messages[messages.length - 1].type === MESSAGE_TYPES.AI
    ) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    // Initialize conversation
    initializeConversation();
  }, []);

  const initializeConversation = () => {
    const initialMessages = [
      {
        id: 1,
        type: MESSAGE_TYPES.AI,
        content:
          "Hello! I'm your AI assistant. I'm here to help you create a quiz. Let's start with the basics.",
        timestamp: new Date(),
      },
      {
        id: 2,
        type: MESSAGE_TYPES.AI,
        content: "What would you like to title your assessment?",
        timestamp: new Date(),
      },
    ];
    setMessages(initialMessages);
  };

  const addMessage = (type, content) => {
    const newMessage = {
      id: Date.now(),
      type,
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  const handleInputSubmit = async () => {
    if (!inputValue.trim()) return;

    const userInput = inputValue.trim();
    setInputValue("");

    // Add user message
    addMessage(MESSAGE_TYPES.USER, userInput);

    // Process based on current step
    switch (currentStep) {
      case CONVERSATION_STEPS.TITLE:
        handleTitleStep(userInput);
        break;
      case CONVERSATION_STEPS.QUESTION_COUNT:
        handleQuestionCountStep(userInput);
        break;
      case CONVERSATION_STEPS.LANGUAGE:
        if (languageStep === "custom") {
          handleCustomLanguageSubmit(userInput);
        }
        break;
      case CONVERSATION_STEPS.REQUIREMENTS:
        handleRequirementsStep(userInput);
        break;
    }
  };

  const handleTitleStep = (title) => {
    setQuizData((prev) => ({ ...prev, title }));

    // AI asks for question count
    setTimeout(() => {
      addMessage(
        MESSAGE_TYPES.AI,
        `Great! "${title}" is a good title. Now, how many questions would you like me to generate? (Maximum: 50)`
      );
      setCurrentStep(CONVERSATION_STEPS.QUESTION_COUNT);
    }, 500);
  };

  const handleQuestionCountStep = (input) => {
    const count = parseInt(input);
    if (isNaN(count) || count < 1 || count > 50) {
      addMessage(
        MESSAGE_TYPES.AI,
        "Please enter a valid number between 1 and 50."
      );
      return;
    }

    setQuizData((prev) => ({ ...prev, questionCount: count }));

    // AI asks for question type
    setTimeout(() => {
      addMessage(
        MESSAGE_TYPES.AI,
        `Perfect! ${count} questions it is. What type of questions would you prefer?`
      );
      setCurrentStep(CONVERSATION_STEPS.QUESTION_TYPE);
    }, 500);
  };

  const handleQuestionTypeChange = (questionType) => {
    if (!questionType) return; // Don't proceed if empty value

    setQuizData((prev) => ({ ...prev, questionType }));

    // AI asks for language
    setTimeout(() => {
      addMessage(
        MESSAGE_TYPES.AI,
        `Excellent choice! Now, in which language should I generate the questions?`
      );
      setCurrentStep(CONVERSATION_STEPS.LANGUAGE);
    }, 500);
  };

  const handleLanguageChange = (language) => {
    if (!language) return; // Don't proceed if empty value

    if (language === "other") {
      // User selected "Other", ask them to specify
      setLanguageStep("custom");
      setTimeout(() => {
        addMessage(
          MESSAGE_TYPES.AI,
          `Please specify the language you'd like me to use for generating the questions.`
        );
      }, 500);
      return;
    }

    // User selected a predefined language
    setQuizData((prev) => ({ ...prev, language }));
    setLanguageStep("select");

    // AI asks for requirements
    setTimeout(() => {
      addMessage(
        MESSAGE_TYPES.AI,
        `Perfect! Now, please tell me about your quiz requirements. What subject, target audience, difficulty level, and specific concepts should I focus on?`
      );
      setCurrentStep(CONVERSATION_STEPS.REQUIREMENTS);
    }, 500);
  };

  const handleCustomLanguageSubmit = (customLanguage) => {
    if (!customLanguage.trim()) {
      addMessage(MESSAGE_TYPES.AI, "Please enter a valid language name.");
      return;
    }

    setQuizData((prev) => ({ ...prev, language: customLanguage.trim() }));
    setLanguageStep("select");

    // AI asks for requirements
    setTimeout(() => {
      addMessage(
        MESSAGE_TYPES.AI,
        `Perfect! I'll generate the questions in ${customLanguage.trim()}. Now, please tell me about your quiz requirements. What subject, target audience, difficulty level, and specific concepts should I focus on?`
      );
      setCurrentStep(CONVERSATION_STEPS.REQUIREMENTS);
    }, 500);
  };

  const handleRequirementsStep = (requirements) => {
    setQuizData((prev) => ({ ...prev, requirements }));

    // AI confirms and asks to generate
    setTimeout(() => {
      const questionTypeText =
        quizData.questionType === "mixed"
          ? "mixed format"
          : quizData.questionType === "multiple_choice"
          ? "multiple choice"
          : "open-ended";

      addMessage(
        MESSAGE_TYPES.AI,
        `Perfect! I will generate ${quizData.questionCount} ${questionTypeText} questions in ${quizData.language} for the topic "${quizData.title}" following your requirements. Shall I proceed?`
      );
      setCurrentStep(CONVERSATION_STEPS.COMPLETE);
    }, 500);
  };

  const generateQuiz = async () => {
    // Validate all required fields are set
    if (
      !quizData.title ||
      !quizData.questionType ||
      !quizData.language ||
      !quizData.requirements
    ) {
      const errorMessage =
        "Missing required information. Please complete all steps.";
      addMessage(MESSAGE_TYPES.ERROR, `❌ ${errorMessage}`);
      setError(errorMessage);
      return;
    }

    setIsGenerating(true);
    // addMessage(MESSAGE_TYPES.LOADING, `Generating ${quizData.questionCount} questions in ${quizData.language}...`);

    try {
      const formData = new FormData();
      formData.append("title", quizData.title);
      formData.append("user_prompt", quizData.requirements);
      formData.append("question_count", quizData.questionCount);
      formData.append("question_type", quizData.questionType);
      formData.append("language", quizData.language);
      formData.append("hints_enabled", 1);
      formData.append("browser_lock", 0); // Default to false for AI-generated quizzes
      if (classId) {
        formData.append("class_id", classId);
      }

      const response = await axiosInstance.post(
        "/quizzes/generate-ai",
        formData
      );

      if (response.data.success) {
        const quizId = response.data.quiz_id;
        addMessage(
          MESSAGE_TYPES.AI,
          `🎉 Quiz generated successfully! Redirecting you to review your questions...`
        );

        // Redirect after a short delay
        setTimeout(() => {
          window.location.href = `/quiz-review/${quizId}`;
        }, 2000);
      } else {
        throw new Error(response.data.message || "Failed to generate quiz.");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "An unexpected error occurred.";
      addMessage(MESSAGE_TYPES.ERROR, `❌ Generation failed: ${errorMessage}`);
      setError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRetry = () => {
    if (retryCount < maxRetries) {
      setRetryCount((prev) => prev + 1);
      setError(null);
      generateQuiz();
    }
  };

  const canRetry = retryCount < maxRetries && error;

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleInputSubmit();
    }
  };

  const renderInputField = () => {
    if (currentStep === CONVERSATION_STEPS.COMPLETE) {
      return (
        <Box className="generate-section">
          <Button
            onClick={generateQuiz}
            disabled={isGenerating}
            variant="contained"
            size="large"
            startIcon={
              isGenerating ? <CircularProgress size={20} /> : <SendIcon />
            }
            className="generate-button"
            fullWidth
          >
            {isGenerating ? "Generating Quiz..." : "Generate Quiz"}
          </Button>
        </Box>
      );
    }

    if (isGenerating) {
      return null;
    }

    if (currentStep === CONVERSATION_STEPS.QUESTION_TYPE) {
      return (
        <FormControl fullWidth className="chat-input-field">
          <InputLabel>Question Type</InputLabel>
          <Select
            value={quizData.questionType}
            label="Question Type"
            onChange={(e) => handleQuestionTypeChange(e.target.value)}
            className="chat-select"
          >
            <MenuItem value="" disabled>
              Choose question type...
            </MenuItem>
            <MenuItem value="mixed">
              Mixed (Multiple Choice + Open-Ended)
            </MenuItem>
            <MenuItem value="multiple_choice">Multiple Choice Only</MenuItem>
            <MenuItem value="open_ended">Open-Ended Only</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (currentStep === CONVERSATION_STEPS.LANGUAGE) {
      if (languageStep === "custom") {
        // Show text input for custom language
        return (
          <TextField
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter the language name..."
            fullWidth
            className="chat-text-input"
            InputProps={{
              endAdornment: (
                <Button
                  onClick={handleInputSubmit}
                  disabled={!inputValue.trim()}
                  variant="contained"
                  size="small"
                  className="send-button"
                >
                  <SendIcon />
                </Button>
              ),
            }}
          />
        );
      }

      // Show language selection dropdown
      return (
        <FormControl fullWidth className="chat-input-field">
          <InputLabel>Language</InputLabel>
          <Select
            value={quizData.language}
            label="Language"
            onChange={(e) => handleLanguageChange(e.target.value)}
            className="chat-select"
          >
            <MenuItem value="" disabled>
              Choose language...
            </MenuItem>
            <MenuItem value="english">English</MenuItem>
            <MenuItem value="arabic">Arabic</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
      );
    }

    // Default text input for title, question count, and requirements
    return (
      <TextField
        ref={inputRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={
          currentStep === CONVERSATION_STEPS.TITLE
            ? "Enter quiz title..."
            : currentStep === CONVERSATION_STEPS.QUESTION_COUNT
            ? "Enter number of questions..."
            : "Describe your quiz requirements..."
        }
        multiline={currentStep === CONVERSATION_STEPS.REQUIREMENTS}
        rows={currentStep === CONVERSATION_STEPS.REQUIREMENTS ? 3 : 1}
        fullWidth
        className="chat-text-input"
        InputProps={{
          endAdornment: (
            <Button
              onClick={handleInputSubmit}
              disabled={!inputValue.trim()}
              variant="contained"
              size="small"
              className="send-button"
            >
              <SendIcon />
            </Button>
          ),
        }}
      />
    );
  };

  const renderMessage = (message) => {
    switch (message.type) {
      case MESSAGE_TYPES.AI:
        return (
          <Box key={message.id} className="message ai-message">
            <Box className="ai-avatar">🤖</Box>
            <Box className="message-content">
              <Typography variant="body1">{message.content}</Typography>
            </Box>
          </Box>
        );

      case MESSAGE_TYPES.USER:
        return (
          <Box key={message.id} className="message user-message">
            <Box className="message-content">
              <Typography variant="body1">{message.content}</Typography>
            </Box>
            <Box className="user-avatar">👤</Box>
          </Box>
        );

      case MESSAGE_TYPES.ERROR:
        return (
          <Box key={message.id} className="message error-message">
            <Box className="ai-avatar">⚠️</Box>
            <Box className="message-content">
              <Alert severity="error" className="error-alert">
                <Typography variant="body1">{message.content}</Typography>
                {canRetry && (
                  <Box className="retry-section">
                    <Typography variant="body2" className="retry-info">
                      Retry {retryCount + 1} of {maxRetries}
                    </Typography>
                    <Button
                      onClick={handleRetry}
                      startIcon={<RefreshIcon />}
                      variant="outlined"
                      size="small"
                      className="retry-button"
                    >
                      Retry Generation
                    </Button>
                  </Box>
                )}
              </Alert>
            </Box>
          </Box>
        );

      case MESSAGE_TYPES.LOADING:
        return (
          <Box key={message.id} className="message ai-message">
            <Box className="ai-avatar">🤖</Box>
            <Box className="message-content">
              <Box className="loading-container">
                <CircularProgress size={20} className="loading-spinner" />
                <Typography variant="body1">{message.content}</Typography>
              </Box>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Header navLinks={navLinks} isScrollEffect={false} />
      <Container className="ai-quiz-container page-container">
        <Typography variant="h4" className="page-title">
          AI Quiz Generator
        </Typography>

        <Paper className="chat-container" elevation={3}>
          {/* Chat Messages */}
          <Box className="chat-messages">
            {messages.map(renderMessage)}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Section */}
          <Box className="chat-input-section">{renderInputField()}</Box>
        </Paper>
      </Container>
    </>
  );
}

export default AIGeneratedQuizPage;
